type AuthRecoveryState = {
  refreshFailureCount: number;
  recoveryCount: number;
  recoveryBlocked: boolean;
};

const STORAGE_KEY = 'grahakAuthRecoveryState';
const INITIAL_STATE: AuthRecoveryState = {
  refreshFailureCount: 0,
  recoveryCount: 0,
  recoveryBlocked: false,
};

let fallbackState = { ...INITIAL_STATE };
let isSessionStorageAvailable: boolean | null = null;
let recoveryInProgress = false;

const normalizeCount = (value: unknown): number => {
  const count = Number(value);
  return Number.isInteger(count) && count >= 0 ? count : 0;
};

const readState = (): AuthRecoveryState => {
  if (typeof window === 'undefined' || isSessionStorageAvailable === false) {
    return fallbackState;
  }

  try {
    const storedValue = window.sessionStorage.getItem(STORAGE_KEY);
    isSessionStorageAvailable = true;
    if (!storedValue) {
      return fallbackState;
    }

    const parsedValue = JSON.parse(storedValue);
    return {
      refreshFailureCount: normalizeCount(parsedValue?.refreshFailureCount),
      recoveryCount: normalizeCount(parsedValue?.recoveryCount),
      recoveryBlocked: parsedValue?.recoveryBlocked === true,
    };
  } catch {
    isSessionStorageAvailable = false;
    return fallbackState;
  }
};

const writeState = (state: AuthRecoveryState): boolean => {
  fallbackState = state;

  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    const persisted = window.sessionStorage.getItem(STORAGE_KEY) !== null;
    isSessionStorageAvailable = persisted;
    return persisted;
  } catch {
    // Storage can be unavailable in private browsing or restricted environments.
    isSessionStorageAvailable = false;
    return false;
  }
};

export const getRefreshFailureCount = (): number => readState().refreshFailureCount;

export const incrementRefreshFailureCount = (): number => {
  const state = readState();
  const refreshFailureCount = state.refreshFailureCount + 1;
  writeState({ ...state, refreshFailureCount });
  return refreshFailureCount;
};

export const resetRefreshFailureCount = (): void => {
  const state = readState();
  writeState({ ...state, refreshFailureCount: 0 });
};

export const getRecoveryCount = (): number => readState().recoveryCount;

export const incrementRecoveryCount = (): number => {
  const state = readState();
  const recoveryCount = state.recoveryCount + 1;
  writeState({ ...state, recoveryCount });
  return recoveryCount;
};

export const isRecoveryBlocked = (): boolean => readState().recoveryBlocked;

export const isAuthRecoveryInProgress = (): boolean => recoveryInProgress;

export const startAuthRecovery = (): void => {
  recoveryInProgress = true;
};

export const finishAuthRecovery = (): void => {
  recoveryInProgress = false;
};

export const blockRecovery = (): void => {
  const state = readState();
  writeState({ ...state, recoveryBlocked: true });
};

export const isAuthRecoveryStatePersistent = (): boolean => writeState(readState());

export const resetAuthRecoveryState = (): void => {
  recoveryInProgress = false;
  writeState({ ...INITIAL_STATE });
};
